import * as React from "react"
import {useState, useCallback} from "react"
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Chip,
    LinearProgress,
    Alert,
    CircularProgress
} from "@mui/material"
import {IconSeeker} from "@ui-seeker/core"
import {postSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS} from "@constants"
import {batchItemRecheckDialog} from "../@signals"


function CandidateImage({src, alt}) {
    const [hasError, setHasError] = useState(false)

    if (!src || hasError) {
        return (
            <Box sx={{width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', borderRadius: 1, flexShrink: 0}}>
                <IconSeeker icon="image" sx={{color: 'text.disabled', fontSize: '1.2rem'}}/>
            </Box>
        )
    }
    return (
        <Box sx={{width: 56, height: 56, overflow: 'hidden', borderRadius: 1, border: theme => `1px solid ${theme.palette.divider}`, flexShrink: 0}}>
            <Box component="img" src={src} alt={alt || ''} onError={() => setHasError(true)} sx={{width: 1, height: 1, objectFit: 'cover'}}/>
        </Box>
    )
}


function ScoreBar({value}) {
    const percent = Math.round((value || 0) * 100)
    const color = percent >= 90 ? 'success' : percent >= 70 ? 'warning' : 'error'
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 100}}>
            <LinearProgress variant="determinate" value={percent} color={color} sx={{flex: 1, height: 8, borderRadius: 4}}/>
            <Typography variant="body2" fontWeight="bold" sx={{minWidth: 40}}>{percent}%</Typography>
        </Box>
    )
}


function CandidateRow({match, index, batchType}) {
    const isRaw2Sku = batchType === 'raw2sku'

    const name = match.name || 'Unknown'
    const score = match.score || 0
    const image = isRaw2Sku
        ? (match.id_sku ? `/backend/sku/image/${match.id_sku}` : null)
        : match.image

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: index === 0 ? 'action.selected' : 'transparent',
            border: theme => index === 0 ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`
        }}>
            {/* Rank */}
            <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{minWidth: 24, textAlign: 'center'}}>
                #{index + 1}
            </Typography>

            {/* Image */}
            <CandidateImage src={image} alt={name}/>

            {/* Name + ID */}
            <Box sx={{flex: 1, overflow: 'hidden'}}>
                <Typography variant="body2" sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                }}>
                    {name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {isRaw2Sku ? `SKU: ${match.id_sku}` : `ID: ${match.product_id}`}
                </Typography>
            </Box>

            {/* Score */}
            <ScoreBar value={score}/>
        </Box>
    )
}


export default function RecheckDialog({fetchItems}) {
    const dialogData = batchItemRecheckDialog.value
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState(null)

    const handleClose = useCallback(() => {
        batchItemRecheckDialog.value = null
        setResult(null)
        setError(null)
        setLoading(false)
    }, [])

    const handleRecheck = useCallback(() => {
        if (!dialogData) return

        const {item, batchType} = dialogData
        const isRaw2Sku = batchType === 'raw2sku'

        const payload = {
            batchType,
            idSite: item.idSite
        }

        if (isRaw2Sku) {
            payload.productId = item.productId
        } else {
            payload.idSku = item.idSku
        }

        setLoading(true)
        setResult(null)
        setError(null)

        postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/items/recheck`, payload)
            .then(res => {
                if (res.error) {
                    setError(res.message || res.error || 'Unknown error from mapping service')
                    if (res.details) {
                        setError(prev => `${prev}\n\nDetails: ${JSON.stringify(res.details, null, 2)}`)
                    }
                } else {
                    setResult(res)
                    // The re-check is a dry run, so nothing changed in the table. Refresh
                    // only if a caller ever asks the mapper to persist (updated_count > 0).
                    if (res.updated_count > 0 && fetchItems) fetchItems()
                }
            })
            .catch(err => {
                const errBody = err?.response?.data || err?.data
                if (errBody?.message) {
                    setError(errBody.message)
                } else if (errBody?.error) {
                    setError(errBody.error)
                } else {
                    setError(err?.message || 'An unexpected error occurred. Please check the console for details.')
                }
                console.error('Recheck error:', err)
            })
            .finally(() => setLoading(false))
    }, [dialogData, fetchItems])

    if (!dialogData) return null

    const {item, batchType} = dialogData
    const isRaw2Sku = batchType === 'raw2sku'
    const open = Boolean(dialogData)

    // Source info
    const sourceName = isRaw2Sku ? (item.productName || item.productId) : (item.skuName || item.idSku)
    const sourceImage = isRaw2Sku ? item.productImage : item.skuImage
    const sourceLabel = isRaw2Sku ? 'Product' : 'SKU'

    const matches = result?.matches || []
    const searchMethod = result?.search_method || result?.mapping_method
    const autoValidated = result?.auto_validated
    const autoExcluded = result?.auto_excluded
    // Dry run: what the mapper *would* do if this ran for real
    const wouldAutoValidate = !autoValidated && result?.should_auto_validate
    const wouldAutoExclude = !autoExcluded && result?.should_auto_exclude
    const diagnostics = result?.diagnostics || []

    // Determine if we should auto-run on open (first render with no result)
    const shouldAutoRun = !result && !error && !loading

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <IconSeeker icon="rotate" sx={{color: 'primary.main'}}/>
                    <Typography variant="h6">Re-check Mapping</Typography>
                </Box>
                <IconButton size="small" onClick={handleClose}>
                    <IconSeeker icon="xmark"/>
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{pb: 3}}>
                {/* Source info */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1}}>
                    <CandidateImage src={sourceImage} alt={sourceName}/>
                    <Box sx={{overflow: 'hidden', flex: 1}}>
                        <Typography variant="caption" color="text.secondary">{sourceLabel}</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                        }}>
                            {sourceName}
                        </Typography>
                    </Box>
                    <Chip label={batchType} size="small" variant="outlined"/>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
                    Preview only: runs the mapper again and shows what it proposes today. The
                    item is not modified.
                </Typography>

                {/* Auto-run on open */}
                {shouldAutoRun && (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                        <Chip
                            label="Run Re-check"
                            color="primary"
                            onClick={handleRecheck}
                            icon={<IconSeeker icon="play"/>}
                            sx={{cursor: 'pointer'}}
                        />
                    </Box>
                )}

                {/* Loading */}
                {loading && (
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4}}>
                        <CircularProgress size={40}/>
                        <Typography variant="body2" color="text.secondary">
                            Running mapping... This may take a moment.
                        </Typography>
                    </Box>
                )}

                {/* Error */}
                {error && !loading && (
                    <Alert severity="error" sx={{mb: 2, whiteSpace: 'pre-wrap'}}>
                        {error}
                    </Alert>
                )}

                {/* Results */}
                {result && !loading && (
                    <Box>
                        {/* Method + status chips */}
                        <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}}>
                            {searchMethod && (
                                <Chip label={`Method: ${searchMethod}`} size="small" color="info" variant="outlined"/>
                            )}
                            {autoValidated && (
                                <Chip label="Auto-validated" size="small" color="success"/>
                            )}
                            {autoExcluded && (
                                <Chip label="Auto-excluded" size="small" color="default"/>
                            )}
                            {wouldAutoValidate && (
                                <Chip label="Would auto-validate" size="small" color="success" variant="outlined"/>
                            )}
                            {wouldAutoExclude && (
                                <Chip label="Would auto-exclude" size="small" color="warning" variant="outlined"/>
                            )}
                            {result.status === 'skipped' && (
                                <Chip label={`Skipped: ${result.reason || ''}`} size="small" color="warning"/>
                            )}
                        </Box>

                        {/* Diagnostics */}
                        {diagnostics.length > 0 && (
                            <Box sx={{mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: theme => `1px solid ${theme.palette.divider}`}}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{mb: 0.5, display: 'block'}}>
                                    Pipeline steps
                                </Typography>
                                {diagnostics.map((step, i) => (
                                    <Box key={i} sx={{display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.25}}>
                                        <Typography variant="caption" color="text.disabled" sx={{minWidth: 16, mt: '1px'}}>
                                            {i + 1}.
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {step}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Skipped message */}
                        {result.status === 'skipped' && (
                            <Alert severity="info" sx={{mb: 2}}>
                                {result.message}
                            </Alert>
                        )}

                        {/* Candidates list */}
                        {matches.length > 0 ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{mb: 0.5}}>
                                    Candidates ({matches.length})
                                </Typography>
                                {matches.map((match, i) => (
                                    <CandidateRow key={match.product_id || match.id_sku || i} match={match} index={i} batchType={batchType}/>
                                ))}
                            </Box>
                        ) : (
                            result.status !== 'skipped' && (
                                <Alert severity="warning">
                                    No candidates found for this mapping.
                                </Alert>
                            )
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    )
}
